import React, { FC, useState } from 'react'
import { Form, Button, Select, InputNumber, message, Empty, Typography } from 'antd'
import { FormComponentProps } from 'antd/lib/form'
import { licensesService } from '../../../services'
import { handleError } from '../../../components'
import { RequestErrorInterface, License, RequestErrorObject, AvailableLicense } from '../../../types'

import styles from './index.module.less'

interface LicenseFormProps extends FormComponentProps {
	availableLicenses: AvailableLicense[]
	customerId: number
	availableLicensesLoading: boolean
	onLicenseAdded(license: License): void
}

const LicenseForm: FC<LicenseFormProps> = ({ customerId, form, availableLicenses, availableLicensesLoading, onLicenseAdded }) => {
	const [addLoading, setAddLoading] = useState<boolean>(false)
	const { getFieldDecorator, validateFields } = form

	const createLicenseAddError = (error: RequestErrorInterface): RequestErrorObject => {
		const source = 'License Add'
		const base = { status: error.response.status, source }

		switch (error.response.status) {
			case 500:
				return { ...base, description: 'Error adding license, experienced server error.' }
			case 422:
				if (error.response.body.quantity) {
					return { ...base, description: error.response.body.quantity[0] }
				}
				return { ...base, description: 'Error adding license, license type may already exist.' }
			default:
				return { ...base, description: 'Error adding license' }
		}
	}

	const onSubmit = (): void => {
		validateFields(
			async (errors, values): Promise<void> => {
				if (errors) {
					message.error(errors)
					return
				}

				setAddLoading(true)
				try {
					const { data } = await licensesService.add(customerId, values.license, values.quantity)
					onLicenseAdded(data)
				} catch (error) {
					handleError(error as Error, createLicenseAddError)
				}
				setAddLoading(false)

				form.resetFields()
			}
		)
	}

	return (
		<Form layout='inline' className={styles.licenseForm}>
			<Form.Item label='License Type'>
				{getFieldDecorator('license', {
					rules: [
						{
							required: true,
							message: 'License is required'
						}
					]
				})(
					<Select
						optionLabelProp='label'
						loading={availableLicensesLoading}
						style={{ width: 300 }}
						notFoundContent={<Empty description='No licenses available' />}
					>
						{availableLicenses.map(lic => (
							<Select.Option key={`${lic.id}`} label={`${lic.display_name}`}>
								<Typography.Text strong>{lic.display_name}</Typography.Text>
							</Select.Option>
						))}
					</Select>
				)}
			</Form.Item>
			<Form.Item label='Quantity'>
				{getFieldDecorator('quantity', {
					rules: [
						{
							required: true,
							message: 'Quantity is required'
						}
					]
				})(<InputNumber min={1} size='default' />)}
			</Form.Item>
			<Form.Item>
				<Button onClick={onSubmit} type='primary' loading={addLoading}>
					Add
				</Button>
			</Form.Item>
		</Form>
	)
}

export default Form.create<LicenseFormProps>()(LicenseForm)
